import { OverflowList } from '@theme/critical';
import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { debounce, isDesktopBreakpoint, mediaQueryLarge, requestYieldCallback } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that displays a product card.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 *
 * @extends {Component<Refs>}
 */
export class ProductCard extends Component {
  requiredRefs = ['productCardLink'];

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    if (typeof this.refs.quickAdd?.fetchProductPage !== 'function') return;
    this.refs.quickAdd.fetchProductPage(this.productPageUrl);
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');
    this.#handleQuickAdd();

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    this.addEventListener('click', this.navigateToProduct);

    // Preload the next image on the slideshow to avoid white flashes on previewImage
    setTimeout(() => {
      if (this.refs.slideshow?.isNested) {
        this.#preloadNextPreviewImage();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.navigateToProduct);
  }

  #preloadNextPreviewImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();

    this.updatePrice(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);
    if (typeof this.refs.quickAdd?.fetchProductPage === 'function') {
      this.refs.quickAdd.fetchProductPage(this.productPageUrl);
    }

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    this.#updateVariantImages();
    this.#previousSlideIndex = null;

    // Remove attribute after re-rendering since a variant selection has been made
    this.removeAttribute('data-no-swatch-selected');

    // Force overflow list to reflow after variant update
    // This fixes an issue where the overflow counter doesn't update properly in some browsers
    this.#updateOverflowList();
  };

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    // Find the overflow list in the variant picker
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    const isActiveOverflowList = overflowList?.querySelector('[slot="overflow"]') ? true : false;
    if (!overflowList || !isActiveOverflowList) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const priceContainer = this.querySelectorAll(`product-price [ref='priceContainer']`)[1];
    const newPriceElement = event.detail.data.html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const anchorElement = event.detail.data.html?.querySelector('product-card a');
    const featuredMediaUrl = event.detail.data.html
      ?.querySelector('product-card-link')
      ?.getAttribute('data-featured-media-url');

    // If the product card is inside a product link, update the product link's featured media URL
    if (featuredMediaUrl && this.closest('product-card-link'))
      this.closest('product-card-link')?.setAttribute('data-featured-media-url', featuredMediaUrl);

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const productUrl = anchorElement.href;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) {
        cardGalleryLink.href = productUrl;
      }
      if (productTitleLink instanceof HTMLAnchorElement) {
        productTitleLink.href = productUrl;
      }
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   */
  #updateVariantImages() {
    const { slideshow } = this.refs;
    if (!this.variantPicker?.selectedOption) {
      return;
    }

    const selectedImageId = this.variantPicker?.selectedOption.dataset.optionMediaId;

    if (slideshow && selectedImageId) {
      const { slides = [] } = slideshow.refs;

      for (const slide of slides) {
        if (slide.getAttribute('variant-image') == null) continue;

        slide.hidden = slide.getAttribute('slide-id') !== selectedImageId;
      }

      slideshow.select({ id: selectedImageId }, undefined, { animate: false });
    }
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }
  /** @type {number | null} */
  #previousSlideIndex = null;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }
  };

  /**
   * Previews a variant.
   * @param {string} id - The id of the variant to preview.
   */
  previewVariant(id) {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();
    slideshow.select({ id }, undefined, { animate: false });
  }

  /**
   * Previews the next image.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();

    // Always preview the second slide on hover.
    const slides = slideshow.refs?.slides || slideshow.slides || [];
    if (slides.length < 2) return;

    slideshow.select(1, undefined, { animate: false });
  }

  /**
   * Resets the image to the variant image.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!slideshow) return;

    // Always return to the first slide on hover leave.
    slideshow.select(0, undefined, { animate: false });
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    // If we have a selected variant, always use its image
    if (this.variantPicker?.selectedOption) {
      const id = this.variantPicker.selectedOption.dataset.optionMediaId;
      if (id) {
        slideshow.select({ id }, undefined, { animate: false });
        return;
      }
    }

    // No variant selected - use initial slide if it's valid
    const initialSlide = slideshow.initialSlide;
    const slideId = initialSlide?.getAttribute('slide-id');
    if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
      return;
    }

    // No valid initial slide or selected variant - go to previous
    slideshow.previous(undefined, { animate: false });
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest(
      'button, input, label, select, [tabindex="1"], ' +
        '[data-type="add-to-cart-form"], [data-action="open-quick-add-modal"], ' +
        '.shoplc-hover-atc, .quick-add, product-form-component'
    );

    // If the click was on an interactive element (or add-to-cart / quick-add), do nothing.
    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const url = new URL(window.location.href);
    const parent = this.closest('li');
    url.hash = productCardAnchor;
    if (parent && parent.dataset.page) {
      url.searchParams.set('page', parent.dataset.page);
    }

    if (!window.Shopify.designMode) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 * @typedef {import('@theme/variant-picker').VariantPickerRefs & {overflowList: HTMLElement}} SwatchesRefs
 */

/**
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest('product-card');

    // Listen for variant updates to apply pending URL changes
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleCardVariantUrlUpdate.bind(this));
  }

  /**
   * Updates the card URL when a variant is selected.
   */
  #handleCardVariantUrlUpdate() {
    if (this.pendingVariantId && this.parentProductCard instanceof ProductCard) {
      const currentUrl = new URL(this.parentProductCard.refs.productCardLink.href);
      currentUrl.searchParams.set('variant', this.pendingVariantId);
      this.parentProductCard.refs.productCardLink.href = currentUrl.toString();
      this.pendingVariantId = null;
    }
  }

  /**
   * Override the variantChanged method to handle unavailable swatches with available alternatives.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Check if this is a swatch input
    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes('-swatch');
    const clickedSwatch = event.target;
    const availableCount = parseInt(clickedSwatch.dataset.availableCount || '0');
    const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    // For swatch inputs, check if we need special handling
    if (isSwatchInput && availableCount > 0 && firstAvailableVariantId) {
      // If this is an unavailable variant but there are available alternatives
      // Prevent the default handling
      event.stopPropagation();

      // Update the selected option visually
      this.updateSelectedOption(clickedSwatch);

      // Build request URL with the first available variant
      const productUrl = this.dataset.productUrl?.split('?')[0];

      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', firstAvailableVariantId);
      url.searchParams.set('section_id', 'section-rendering-product-card');

      const requestUrl = url.href;

      // Store the variant ID we want to apply to the URL
      this.pendingVariantId = firstAvailableVariantId;

      // Use parent's fetch method
      this.fetchUpdatedSection(requestUrl);
      return;
    }

    // For all other cases, use the default behavior
    super.variantChanged(event);
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}

(() => {
  /**
   * Fetch and render product-card variant media on hover/select.
   *
   * Instead of trying to infer variant galleries from the PLP markup, we ask Shopify
   * for a tiny server-rendered payload (Section Rendering API) and then update the
   * existing card-gallery slideshow in place.
   *
   * Required markup:
   * - Swatch element: `[data-card-swatch][data-variant-id]`
   * - Product link inside card: `a[ref="productCardLink"]` (preferred), or `.shoplc-product-card[data-product-url]`
   * - Server section: `sections/section-rendering-product-card-media.liquid`
   */

  /** @type {Map<string, { media: any[], count: number }>} */
  const cache = new Map();

  /** @type {WeakMap<HTMLElement, AbortController>} */
  const abortByCard = new WeakMap();

  /** @type {WeakMap<HTMLElement, HTMLElement>} */
  const lastSwatchByCard = new WeakMap();

  /** @type {WeakSet<HTMLElement>} */
  const initializedCards = new WeakSet();

  /**
   * Initial (page-load) state snapshot per card so we can restore it when the mouse leaves the card.
   * @type {WeakMap<
   *   HTMLElement,
   *   {
   *     variantId: string | null;
   *     media: any[];
   *     productCardLinkHref: string | null;
   *     cardGalleryLinkHref: string | null;
   *   }
   * >}
   */
  const initialStateByCard = new WeakMap();

  function getSlides(slideshow) {
    return Array.from(slideshow.querySelectorAll('slideshow-slide[ref="slides[]"]')).filter(
      (el) => el instanceof HTMLElement
    );
  }

  function getDotButtons(slideshow) {
    return Array.from(slideshow.querySelectorAll('[ref="dots[]"]')).filter((el) => el instanceof HTMLElement);
  }

  function getThumbnailButtons(slideshow) {
    return Array.from(slideshow.querySelectorAll('[ref="thumbnails[]"]')).filter((el) => el instanceof HTMLElement);
  }

  function syncControls(slideshow, visibleCount) {
    const dots = getDotButtons(slideshow);
    dots.forEach((btn, index) => {
      const li = btn.closest('li');
      const shouldShow = index < visibleCount;
      if (li) li.hidden = !shouldShow;
      btn.hidden = !shouldShow;
      if (shouldShow) {
        btn.setAttribute('on:click', `/select/${index}`);
        btn.textContent = String(index + 1);
      }
    });

    const thumbnails = getThumbnailButtons(slideshow);
    thumbnails.forEach((btn, index) => {
      const li = btn.closest('li');
      const shouldShow = index < visibleCount;
      if (li) li.hidden = !shouldShow;
      btn.hidden = !shouldShow;
      if (shouldShow) {
        btn.setAttribute('on:click', `/select/${index}`);
      }
    });
  }

  function updateSlideFromMedia(slide, media) {
    if (!(slide instanceof HTMLElement)) return;
    slide.removeAttribute('reveal');

    if (!media) {
      slide.hidden = true;
      return;
    }

    slide.hidden = false;
    if (media.id) slide.setAttribute('slide-id', String(media.id));

    const productMedia = slide.querySelector('[data-media-id]');
    if (productMedia instanceof HTMLElement && media.id) {
      productMedia.setAttribute('data-media-id', String(media.id));
    }

    const img =
      slide.querySelector('img.product-media__image') ||
      slide.querySelector('img');

    if (!(img instanceof HTMLImageElement)) return;

    if (media.alt != null) img.alt = String(media.alt);
    if (media.src) img.src = String(media.src);
    if (media.srcset) img.srcset = String(media.srcset);
    if (media.maxResolution) img.dataset.maxResolution = String(media.maxResolution);
    img.removeAttribute('loading');
  }

  function applyMediaToSlideshow(slideshow, mediaList) {
    if (!(slideshow instanceof HTMLElement)) return;
    const slides = getSlides(slideshow);
    if (!slides.length) return;

    const visibleCount = Math.min(mediaList.length, slides.length);

    for (let i = 0; i < slides.length; i++) {
      updateSlideFromMedia(slides[i], mediaList[i]);
    }

    syncControls(slideshow, visibleCount);

    /** @type {any} */ (slideshow).select?.(0, undefined, { animate: false });
  }

  async function fetchVariantMedia(productUrl, variantId, signal) {
    const baseKey = (() => {
      try {
        const u = new URL(productUrl, window.location.origin);
        return `${u.origin}${u.pathname}`;
      } catch {
        return String(productUrl || '').split('?')[0];
      }
    })();
    const key = `${baseKey}::${variantId}`;
    const cached = cache.get(key);
    if (cached) return cached;

    const url = new URL(productUrl, window.location.origin);
    url.searchParams.set('variant', String(variantId));
    url.searchParams.set('section_id', 'section-rendering-product-card-media');

    const requestUrl = url.toString();
    const res = await fetch(requestUrl, { signal, credentials: 'same-origin' });
    const text = await res.text();

    /** @type {HTMLElement | null} */
    let root = null;

    // Primary: section_id HTML response.
    try {
      const html = new DOMParser().parseFromString(text, 'text/html');
      root = html.querySelector('[data-product-card-variant-media]');
    } catch {
      // ignore
    }

    // Fallback: some setups respond with the "sections" JSON format.
    if (!root && text.trim().startsWith('{')) {
      try {
        const json = JSON.parse(text);
        const maybeHtml =
          json?.['section-rendering-product-card-media'] ||
          json?.['shopify-section-section-rendering-product-card-media'] ||
          (typeof json === 'object' ? Object.values(json).find((v) => typeof v === 'string') : null);
        if (typeof maybeHtml === 'string') {
          const html = new DOMParser().parseFromString(maybeHtml, 'text/html');
          root = html.querySelector('[data-product-card-variant-media]');
        }
      } catch {
        // ignore
      }
    }

    if (!(root instanceof HTMLElement)) {
      console.warn(
        '[product-card] Missing [data-product-card-variant-media] in response',
        { requestUrl, status: res.status }
      );
      console.warn(text.slice(0, 500));
      return { media: [], count: 0 };
    }

    const items = Array.from(root.querySelectorAll('[data-media]')).filter((el) => el instanceof HTMLElement);
    const media = items.map((el) => ({
      id: el.dataset.id,
      src: el.dataset.src,
      srcset: el.dataset.srcset,
      alt: el.dataset.alt,
      maxResolution: el.dataset.maxResolution,
    }));

    const payload = { media, count: media.length };
    cache.set(key, payload);
    return payload;
  }

  function updateSelectedSwatch(card, swatch) {
    card.querySelectorAll('.shoplc-product-card__variant-swatch--selected').forEach((el) => {
      el.classList.remove('shoplc-product-card__variant-swatch--selected');
    });
    swatch.classList.add('shoplc-product-card__variant-swatch--selected');
    const input = swatch.querySelector('input[type="radio"]');
    if (input instanceof HTMLInputElement) input.checked = true;
  }

  function updateCardLinkToVariant(card, variantId) {
    const productCard = card.querySelector('product-card');
    if (!(productCard instanceof HTMLElement)) return;
    const links = productCard.querySelectorAll('a[ref="productCardLink"], a[ref="cardGalleryLink"], a[ref="productTitleLink"]');
    links.forEach((link) => {
      if (!(link instanceof HTMLAnchorElement)) return;
      if (!link.href) return;
      const url = new URL(link.href, window.location.origin);
      url.searchParams.set('variant', String(variantId));
      link.href = url.toString();
    });
  }

  function updateQuickAddToVariant(card, variantId) {
    if (!variantId) return;

    card.dataset.selectedVariantId = String(variantId);

    const drawer = document.getElementById('quick-add-drawer');
    if (drawer) {
      drawer.setAttribute('data-forced-variant-id', String(variantId));
    }

    card.querySelectorAll(
      '.shoplc-product-card__quick-add-cta, .shoplc-product-card__variant-overflow'
    ).forEach((button) => {
      button.setAttribute('data-variant-id', String(variantId));
    });

    const quickAdd = card.querySelector('quick-add-component');
    const productUrl = getProductUrl(card);

    if (quickAdd) {
      quickAdd.dataset.selectedVariantId = String(variantId);

      if (productUrl) {
        const url = new URL(productUrl, window.location.origin);
        url.searchParams.set('variant', String(variantId));
        quickAdd.dataset.productUrl = url.toString();
      }
    }
  }

  function captureInitialState(card, slideshow) {
    if (initialStateByCard.has(card)) return initialStateByCard.get(card);

    const selectedSwatch =
      card.querySelector('[data-card-swatch].shoplc-product-card__variant-swatch--selected') ||
      card
        .querySelector('[data-card-swatch] input[type="radio"]:checked')
        ?.closest('[data-card-swatch]');

    const variantIdRaw =
      selectedSwatch instanceof HTMLElement ? String(selectedSwatch.getAttribute('data-variant-id') || '').trim() : '';
    const variantId = variantIdRaw || null;

    const productCard = card.querySelector('product-card');
    let productCardLinkHref = null;
    let cardGalleryLinkHref = null;
    let productTitleLinkHref = null;
    if (productCard instanceof HTMLElement) {
      const productLink = productCard.querySelector('a[ref="productCardLink"]');
      if (productLink instanceof HTMLAnchorElement && productLink.href) {
        productCardLinkHref = productLink.href;
      }

      const galleryLink = productCard.querySelector('a[ref="cardGalleryLink"]');
      if (galleryLink instanceof HTMLAnchorElement && galleryLink.href) {
        cardGalleryLinkHref = galleryLink.href;
      }

      const titleLink = productCard.querySelector('a[ref="productTitleLink"]');
      if (titleLink instanceof HTMLAnchorElement && titleLink.href) {
        productTitleLinkHref = titleLink.href;
      }
    }

    const media = [];
    if (slideshow instanceof HTMLElement) {
      const slides = getSlides(slideshow);
      for (const slide of slides) {
        if (slide.hidden) break;
        const img = slide.querySelector('img.product-media__image') || slide.querySelector('img');
        if (!(img instanceof HTMLImageElement) || !img.src) break;

        const id =
          slide.getAttribute('slide-id') ||
          slide.querySelector('[data-media-id]')?.getAttribute('data-media-id') ||
          '';

        media.push({
          id: id || undefined,
          src: img.src,
          srcset: img.getAttribute('srcset') || img.srcset || undefined,
          alt: img.alt || undefined,
          maxResolution: img.dataset.maxResolution || undefined,
        });
      }
    }

    const priceContainer = getPriceContainer(card);
    const titleEl = getTitleElement(card);

    const initialPriceHtml = priceContainer?.innerHTML ?? null;
    const initialTitle = titleEl?.textContent ?? null;

    const state = {
      variantId,
      media,
      productCardLinkHref,
      cardGalleryLinkHref,
      productTitleLinkHref,
      initialPriceHtml,
      initialTitle,
    };

    initialStateByCard.set(card, state);
    return state;
  }

  function restoreInitialState(card) {
    const state = initialStateByCard.get(card);
    if (!state) return;

    const productCard = card.querySelector('product-card');
    if (!(productCard instanceof HTMLElement)) return;

    const gallery = productCard.querySelector('.card-gallery');
    if (!(gallery instanceof HTMLElement)) return;

    const slideshow = gallery.querySelector('slideshow-component');
    if (!(slideshow instanceof HTMLElement)) return;

    abortByCard.get(card)?.abort();

    if (state.variantId) {
      const defaultSwatch = card.querySelector(`[data-card-swatch][data-variant-id="${state.variantId}"]`);
      if (defaultSwatch instanceof HTMLElement) {
        updateSelectedSwatch(card, defaultSwatch);
        lastSwatchByCard.set(card, defaultSwatch);
      }
    }

    if (state.productCardLinkHref || state.cardGalleryLinkHref || state.productTitleLinkHref) {
      const links = productCard.querySelectorAll('a[ref="productCardLink"], a[ref="cardGalleryLink"], a[ref="productTitleLink"]');
      links.forEach((link) => {
        if (!(link instanceof HTMLAnchorElement)) return;
        const ref = link.getAttribute('ref');
        if (ref === 'productCardLink' && state.productCardLinkHref) link.href = state.productCardLinkHref;
        if (ref === 'cardGalleryLink' && state.cardGalleryLinkHref) link.href = state.cardGalleryLinkHref;
        if (ref === 'productTitleLink' && state.productTitleLinkHref) link.href = state.productTitleLinkHref;
      });
    } else if (state.variantId) {
      updateCardLinkToVariant(card, state.variantId);
    }

    if (state.media?.length) {
      applyMediaToSlideshow(slideshow, state.media);
    }

    // Restore original price
    const priceContainer = getPriceContainer(card);
    if (priceContainer && state.initialPriceHtml != null) {
      priceContainer.innerHTML = state.initialPriceHtml;
    }

    // Restore original title
    const titleEl = getTitleElement(card);
    if (titleEl && state.initialTitle != null) {
      titleEl.textContent = state.initialTitle;
    }
  }

  function getProductUrl(card) {
    const productCard = card.querySelector('product-card');
    if (productCard instanceof HTMLElement) {
      const link =
        productCard.querySelector('a[ref="productCardLink"]') ||
        productCard.querySelector('a[ref="cardGalleryLink"]');
      if (link instanceof HTMLAnchorElement && link.href) {
        return link.href;
      }
    }
    return card.getAttribute('data-product-url') || '';
  }

  function isSoldOutSwatch(swatch) {
    return swatch.classList.contains('shoplc-product-card__variant-swatch--sold-out');
  }

  /**
   * Gets the price container element within a product card.
   * @param {HTMLElement} card
   * @returns {HTMLElement | null}
   */
  function getPriceContainer(card) {
    return card.querySelector('.shoplc-price-row[ref="priceContainer"]');
  }

  /**
   * Gets the title text element within a product card.
   * @param {HTMLElement} card
   * @returns {HTMLElement | null}
   */
  function getTitleElement(card) {
    // The title is rendered inside a <p> inside [ref="productTitleLink"] or the text block
    return (
      card.querySelector('a[ref="productTitleLink"] .text-block p') ||
      card.querySelector('a[ref="productTitleLink"] p')
    );
  }

  /**
   * Formats a price in cents using the shop's money format stored on the page.
   * Falls back to a simple "€X,XX" style if Shopify.formatMoney is unavailable.
   * @param {number} cents
   * @returns {string}
   */
  function formatMoney(cents) {
    if (typeof window.Shopify?.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, window.Shopify.money_format);
    }
    // Simple fallback: format as European-style price
    const euros = (cents / 100).toFixed(2).replace('.', ',');
    return `€${euros}`;
  }

  /**
   * Builds an updated price row HTML string matching the structure of snippets/price.liquid.
   * @param {number} price - Sale price in cents
   * @param {number} compareAtPrice - Compare-at price in cents (0 = none)
   * @returns {string}
   */
  function buildPriceHtml(price, compareAtPrice) {
    const hasCompare = compareAtPrice > 0 && compareAtPrice > price;
    const priceFormatted = formatMoney(price);
    const compareFormatted = hasCompare ? formatMoney(compareAtPrice) : '';
    const discountPct = hasCompare
      ? Math.floor(((compareAtPrice - price) * 100) / compareAtPrice + 0.5)
      : 0;

    let html = '';
    if (hasCompare) {
      html += `
        <span role="group">
          <span class="visually-hidden">&nbsp;</span>
          <span class="price">${priceFormatted}</span>
        </span>
      `;

      html += `
        <span class="shoplc-price-gap">&nbsp;</span>
        <span role="group">
          <span class="visually-hidden">&nbsp;</span>
          <span class="compare-at-price">${compareFormatted}</span>
        </span>
      `;

      if (discountPct > 0) {
        html += `
          <span class="shoplc-price-gap">&nbsp;</span>
          <span class="shoplc-percentage-discount" aria-hidden="true">
            (-${discountPct}%)
          </span>
        `;
      }
    } else {
      html += `<span class="price">${priceFormatted}</span>`;
    }
    return html;
  }

  /**
   * Updates the price row in the card for the hovered swatch.
   * @param {HTMLElement} card
   * @param {HTMLElement} swatch - The [data-card-swatch] element (the <li>)
   */
  function updateCardPrice(card, swatch) {
    const priceContainer = getPriceContainer(card);
    if (!priceContainer) return;
    const rawPrice = swatch?.dataset?.variantPrice;
    const rawCompareAtPrice = swatch?.dataset?.variantComparePrice;

    if (!rawPrice) {
      console.warn('[PLP Price] Missing variant price', {
        variantId: swatch?.dataset?.variantId,
        swatch,
      });
      return;
    }

    const price = parseInt(rawPrice, 10);
    const compareAtPrice = parseInt(rawCompareAtPrice || '0', 10);

    if (Number.isNaN(price)) {
      console.warn('[PLP Price] Invalid variant price', {
        rawPrice,
        variantId: swatch?.dataset?.variantId,
      });
      return;
    }

    priceContainer.innerHTML = buildPriceHtml(
      price,
      compareAtPrice
    );
  }

  /**
   * Updates the title text in the card for the hovered swatch.
   * @param {HTMLElement} card
   * @param {HTMLElement} swatch - The [data-card-swatch] element
   */
  function updateCardTitle(card, swatch) {
    const titleElement = card.querySelector('.text-block p');
    const variantTitle = swatch?.dataset?.variantTitle?.trim();
    titleElement.textContent = variantTitle;
}

  async function handleSwatch(card, swatch) {
    if (isSoldOutSwatch(swatch)) return;

    const variantId = String(swatch.getAttribute('data-variant-id') || '').trim();
    if (!variantId) return;

    const productUrl = getProductUrl(card);
    if (!productUrl) return;

    const productCard = card.querySelector('product-card');
    if (!(productCard instanceof HTMLElement)) return;

    const gallery = productCard.querySelector('.card-gallery');
    if (!(gallery instanceof HTMLElement)) return;

    const slideshow = gallery.querySelector('slideshow-component');

    captureInitialState(card, slideshow);

    updateSelectedSwatch(card, swatch);
    updateCardLinkToVariant(card, variantId);
    updateQuickAddToVariant(card, variantId);

    // Update price and title immediately from embedded swatch data (no fetch needed)
    updateCardPrice(card, swatch);
    updateCardTitle(card, swatch);

    // If the gallery uses slideshow-component, we must fetch and apply the media here.
    // If it uses card-gallery-lazy (e.g. on PLP), it natively handles media fetching on its own.
    if (slideshow instanceof HTMLElement) {
      abortByCard.get(card)?.abort();
      const controller = new AbortController();
      abortByCard.set(card, controller);

      try {
        const { media } = await fetchVariantMedia(productUrl, variantId, controller.signal);
        if (!media?.length) return;
        applyMediaToSlideshow(slideshow, media);
      } catch (err) {
        if (/** @type {any} */ (err)?.name === 'AbortError') return;
        console.error(err);
      }
    }
  }

  function getSelectedCardSwatch(card) {
    const selectedSwatch =
      card.querySelector('[data-card-swatch].shoplc-product-card__variant-swatch--selected') ||
      card
        .querySelector('[data-card-swatch] input[type="radio"]:checked')
        ?.closest('[data-card-swatch]');

    if (selectedSwatch instanceof HTMLElement) return selectedSwatch;

    const firstAvailableSwatch = card.querySelector('[data-card-swatch][data-variant-id]:not(.shoplc-product-card__variant-swatch--sold-out)');
    return firstAvailableSwatch instanceof HTMLElement ? firstAvailableSwatch : null;
  }

  function initializeCardSwatchState(card) {
    if (initializedCards.has(card)) return;

    const swatch = getSelectedCardSwatch(card);
    if (!(swatch instanceof HTMLElement) || isSoldOutSwatch(swatch)) return;

    const variantId = String(swatch.getAttribute('data-variant-id') || '').trim();
    if (!variantId) return;

    const productUrl = getProductUrl(card);
    if (!productUrl) return;

    const productCard = card.querySelector('product-card');
    if (!(productCard instanceof HTMLElement)) return;

    const gallery = productCard.querySelector('.card-gallery');
    if (!(gallery instanceof HTMLElement)) return;

    const slideshow = gallery.querySelector('slideshow-component');
    if (!(slideshow instanceof HTMLElement)) return;

    initializedCards.add(card);
    updateSelectedSwatch(card, swatch);
    updateCardLinkToVariant(card, variantId);
    updateQuickAddToVariant(card, variantId);
    captureInitialState(card, slideshow);
  }

  function initializeProductCardSwatches(root = document) {
    const cards = [];
    if (root instanceof HTMLElement && root.matches('.shoplc-product-card')) {
      cards.push(root);
    }
    cards.push(...Array.from(root.querySelectorAll('.shoplc-product-card')).filter((el) => el instanceof HTMLElement));

    cards.forEach((card) => {
      initializeCardSwatchState(card);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeProductCardSwatches(), { once: true });
  } else {
    initializeProductCardSwatches();
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    initializeProductCardSwatches(event.target);
  });

  document.addEventListener('filter:update-complete', () => initializeProductCardSwatches());

  document.addEventListener('product-grid:deferred-items-loaded', (event) => {
    const root = event.detail?.grid instanceof HTMLElement ? event.detail.grid : document;
    initializeProductCardSwatches(root);
  });

  document.addEventListener(
    'pointerover',
    (event) => {
      if (!(event instanceof PointerEvent) || event.pointerType !== 'mouse') return;
      if (!(event.target instanceof Element)) return;

      const swatch = event.target.closest('[data-card-swatch][data-variant-id]');
      if (!(swatch instanceof HTMLElement)) return;

      if (isSoldOutSwatch(swatch)) return;

      const card = swatch.closest('.shoplc-product-card');
      if (!(card instanceof HTMLElement)) return;

      const last = lastSwatchByCard.get(card);
      if (last === swatch) return;
      lastSwatchByCard.set(card, swatch);

      handleSwatch(card, swatch);
    },
    { capture: true }
  );

  document.addEventListener(
    'change',
    (event) => {
      if (!(event.target instanceof Element)) return;
      const swatch = event.target.closest('[data-card-swatch][data-variant-id]');
      if (!(swatch instanceof HTMLElement)) return;
      if (isSoldOutSwatch(swatch)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      const card = swatch.closest('.shoplc-product-card');
      if (!(card instanceof HTMLElement)) return;
      lastSwatchByCard.set(card, swatch);
      handleSwatch(card, swatch);
    },
    { capture: true }
  );

  document.addEventListener(
    'pointerout',
    (event) => {
      if (!(event instanceof PointerEvent) || event.pointerType !== 'mouse') return;
      if (!(event.target instanceof Element)) return;

      const card = event.target.closest('.shoplc-product-card');
      if (!(card instanceof HTMLElement)) return;

      if (event.relatedTarget instanceof Node && card.contains(event.relatedTarget)) return;

      const state = initialStateByCard.get(card);
      if (!state?.variantId) return;

      const defaultSwatch = card.querySelector(`[data-card-swatch][data-variant-id="${state.variantId}"]`);
      if (!(defaultSwatch instanceof HTMLElement)) return;

      const last = lastSwatchByCard.get(card);
      if (!last || last === defaultSwatch) return;

      restoreInitialState(card);
    },
    { capture: true }
  );
})();
